import type { DailyHealthRecord, DailyHealthStoreSaveResult, HealthImportSession } from "./dailyHealthTypes";

const DAILY_HEALTH_DB_NAME = "johan-performance-lab-daily-health";
const DAILY_HEALTH_DB_VERSION = 1;
const DAILY_RECORDS_STORE = "daily-health-records";
const IMPORT_SESSIONS_STORE = "health-import-sessions";
const IMPORT_FINGERPRINTS_STORE = "health-import-fingerprints";

type StoreStatus = "unknown" | "opening" | "open" | "failed";
type Backend = "indexeddb" | "memory";

type OpenStoreResult = {
  backend: Backend;
  databaseStatus: StoreStatus;
  database: IDBDatabase | null;
};

type FingerprintRecord = {
  fingerprint: string;
  lastSessionId: string;
  updatedAt: string;
};

const memoryDailyRecords = new Map<string, DailyHealthRecord>();
const memoryImportSessions = new Map<string, HealthImportSession>();
const memoryFingerprints = new Map<string, FingerprintRecord>();

let backend: Backend = "indexeddb";
let databaseStatus: StoreStatus = "unknown";
let database: IDBDatabase | null = null;
let opening: Promise<OpenStoreResult> | null = null;

function cloneRecord(record: DailyHealthRecord): DailyHealthRecord {
  return JSON.parse(JSON.stringify(record)) as DailyHealthRecord;
}

function cloneSession(session: HealthImportSession): HealthImportSession {
  return JSON.parse(JSON.stringify(session)) as HealthImportSession;
}

async function initializeStore(): Promise<OpenStoreResult> {
  if (databaseStatus === "open" && database) {
    return { backend, databaseStatus, database };
  }
  if (opening) {
    return opening;
  }

  databaseStatus = "opening";
  if (typeof indexedDB === "undefined") {
    backend = "memory";
    databaseStatus = "failed";
    return { backend, databaseStatus, database: null };
  }

  opening = new Promise(resolve => {
    const request = indexedDB.open(DAILY_HEALTH_DB_NAME, DAILY_HEALTH_DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(DAILY_RECORDS_STORE)) {
        db.createObjectStore(DAILY_RECORDS_STORE, { keyPath: "localDate" });
      }
      if (!db.objectStoreNames.contains(IMPORT_SESSIONS_STORE)) {
        db.createObjectStore(IMPORT_SESSIONS_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(IMPORT_FINGERPRINTS_STORE)) {
        db.createObjectStore(IMPORT_FINGERPRINTS_STORE, { keyPath: "fingerprint" });
      }
    };
    request.onerror = () => {
      backend = "memory";
      databaseStatus = "failed";
      opening = null;
      resolve({ backend, databaseStatus, database: null });
    };
    request.onblocked = () => {
      backend = "memory";
      databaseStatus = "failed";
      opening = null;
      resolve({ backend, databaseStatus, database: null });
    };
    request.onsuccess = () => {
      backend = "indexeddb";
      databaseStatus = "open";
      database = request.result;
      database.onclose = () => {
        databaseStatus = "failed";
        database = null;
      };
      opening = null;
      resolve({ backend, databaseStatus, database });
    };
  });

  return opening;
}

function runTransaction<T>(
  db: IDBDatabase,
  mode: IDBTransactionMode,
  operation: (transaction: IDBTransaction, resolve: (value: T) => void, reject: (error: unknown) => void) => void
): Promise<T> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction([DAILY_RECORDS_STORE, IMPORT_SESSIONS_STORE, IMPORT_FINGERPRINTS_STORE], mode);
    operation(tx, resolve, reject);
    tx.onerror = () => reject(tx.error ?? new Error("IndexedDB transaction failed."));
  });
}

export async function getDailyHealthStoreStatus(): Promise<{ backend: Backend; databaseStatus: StoreStatus }> {
  const opened = await initializeStore();
  return { backend: opened.backend, databaseStatus: opened.databaseStatus };
}

export async function upsertDailyHealthRecords(records: DailyHealthRecord[]): Promise<DailyHealthStoreSaveResult> {
  const opened = await initializeStore();
  const existing = await listDailyHealthRecords();
  const existingByDate = new Map(existing.map(record => [record.localDate, record]));

  let created = 0;
  let updated = 0;

  const merged = records.map(record => {
    const prior = existingByDate.get(record.localDate);
    if (!prior) {
      created += 1;
      return record;
    }
    updated += 1;
    return {
      ...record,
      createdAt: prior.createdAt,
      updatedAt: new Date().toISOString()
    };
  });

  if (opened.database) {
    await runTransaction<void>(opened.database, "readwrite", (tx, resolve, reject) => {
      const store = tx.objectStore(DAILY_RECORDS_STORE);
      for (const record of merged) {
        store.put(record);
      }
      tx.oncomplete = () => resolve(undefined);
      tx.onerror = () => reject(tx.error ?? new Error("Unable to upsert daily health records."));
    });
  } else {
    for (const record of merged) {
      memoryDailyRecords.set(record.localDate, cloneRecord(record));
    }
  }

  return {
    backend: opened.backend,
    databaseStatus: opened.databaseStatus,
    dailyRecordsCreated: created,
    dailyRecordsUpdated: updated
  };
}

export async function listDailyHealthRecords(): Promise<DailyHealthRecord[]> {
  const opened = await initializeStore();
  if (opened.database) {
    return runTransaction<DailyHealthRecord[]>(opened.database, "readonly", (tx, resolve) => {
      const request = tx.objectStore(DAILY_RECORDS_STORE).getAll();
      request.onerror = () => resolve([]);
      request.onsuccess = () => {
        const values = ((request.result as DailyHealthRecord[] | undefined) ?? []).map(cloneRecord);
        resolve(values.sort((left, right) => right.localDate.localeCompare(left.localDate)));
      };
    });
  }

  return [...memoryDailyRecords.values()].map(cloneRecord).sort((left, right) => right.localDate.localeCompare(left.localDate));
}

export async function saveHealthImportSession(session: HealthImportSession): Promise<void> {
  const opened = await initializeStore();
  if (opened.database) {
    await runTransaction<void>(opened.database, "readwrite", (tx, resolve, reject) => {
      tx.objectStore(IMPORT_SESSIONS_STORE).put(session);
      tx.objectStore(IMPORT_FINGERPRINTS_STORE).put({
        fingerprint: session.importFingerprint,
        lastSessionId: session.id,
        updatedAt: session.completedAt ?? session.startedAt
      } satisfies FingerprintRecord);
      tx.oncomplete = () => resolve(undefined);
      tx.onerror = () => reject(tx.error ?? new Error("Unable to save import session."));
    });
    return;
  }

  memoryImportSessions.set(session.id, cloneSession(session));
  memoryFingerprints.set(session.importFingerprint, {
    fingerprint: session.importFingerprint,
    lastSessionId: session.id,
    updatedAt: session.completedAt ?? session.startedAt
  });
}

export async function listHealthImportSessions(): Promise<HealthImportSession[]> {
  const opened = await initializeStore();
  if (opened.database) {
    return runTransaction<HealthImportSession[]>(opened.database, "readonly", (tx, resolve) => {
      const request = tx.objectStore(IMPORT_SESSIONS_STORE).getAll();
      request.onerror = () => resolve([]);
      request.onsuccess = () => {
        const values = ((request.result as HealthImportSession[] | undefined) ?? []).map(cloneSession);
        resolve(values.sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt)));
      };
    });
  }

  return [...memoryImportSessions.values()].map(cloneSession).sort((left, right) => Date.parse(right.startedAt) - Date.parse(left.startedAt));
}

export async function getImportSessionByFingerprint(fingerprint: string): Promise<HealthImportSession | null> {
  const opened = await initializeStore();

  if (opened.database) {
    const pointer = await runTransaction<FingerprintRecord | null>(opened.database, "readonly", (tx, resolve) => {
      const request = tx.objectStore(IMPORT_FINGERPRINTS_STORE).get(fingerprint);
      request.onerror = () => resolve(null);
      request.onsuccess = () => resolve((request.result as FingerprintRecord | undefined) ?? null);
    });

    if (!pointer) {
      return null;
    }

    return runTransaction<HealthImportSession | null>(opened.database, "readonly", (tx, resolve) => {
      const request = tx.objectStore(IMPORT_SESSIONS_STORE).get(pointer.lastSessionId);
      request.onerror = () => resolve(null);
      request.onsuccess = () => resolve((request.result as HealthImportSession | undefined) ?? null);
    });
  }

  const pointer = memoryFingerprints.get(fingerprint);
  if (!pointer) {
    return null;
  }
  const session = memoryImportSessions.get(pointer.lastSessionId);
  return session ? cloneSession(session) : null;
}

export async function clearDailyHealthStore(): Promise<void> {
  const opened = await initializeStore();
  if (opened.database) {
    await runTransaction<void>(opened.database, "readwrite", (tx, resolve) => {
      tx.objectStore(DAILY_RECORDS_STORE).clear();
      tx.objectStore(IMPORT_SESSIONS_STORE).clear();
      tx.objectStore(IMPORT_FINGERPRINTS_STORE).clear();
      tx.oncomplete = () => resolve(undefined);
      tx.onerror = () => resolve(undefined);
    });
    return;
  }

  memoryDailyRecords.clear();
  memoryImportSessions.clear();
  memoryFingerprints.clear();
}
